import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { useMatchReminders } from "../hooks/useMatchReminders";

const DISMISS_KEY_PREFIX = "match_reminder_dismissed_";

/**
 * Sitewide top banner that appears when a signed-in user has a Men's First
 * Team fixture kicking off in the next 2 hours they haven't predicted yet.
 * Dismissible per-fixture, per-browser (localStorage).
 */
export function MatchReminderBanner() {
  const { reminders } = useMatchReminders();
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    // Rebuild dismissed lookup from localStorage on mount
    const state = {};
    for (const r of reminders) {
      state[r.id] = localStorage.getItem(DISMISS_KEY_PREFIX + r.id) === "1";
    }
    setDismissed(state);
  }, [reminders]);

  const visible = reminders.find((r) => !dismissed[r.id]);
  if (!visible) return null;

  const kickoff = new Date(visible.date);
  const timeLabel =
    visible.time ||
    kickoff.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/London",
    });

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY_PREFIX + visible.id, "1");
    setDismissed((prev) => ({ ...prev, [visible.id]: true }));
  };

  return (
    <div
      className="bg-gradient-to-r from-[#F8D742] to-yellow-300 text-[#0F172A] border-b border-yellow-400"
      data-testid="match-reminder-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 flex-wrap">
        <Bell className="w-4 h-4 flex-shrink-0" />
        <p className="text-xs sm:text-sm font-semibold flex-1 min-w-0">
          Kickoff in under 2 hours — West Ham v {visible.opponent} at {timeLabel}. You haven't submitted a prediction yet.
        </p>
        <Link
          to="/score-predictor"
          className="text-xs font-bold bg-[#7C2C3B] text-white px-3 py-1 rounded hover:bg-[#5a1f2b] transition-colors whitespace-nowrap"
          data-testid="match-reminder-banner-cta"
        >
          Predict now →
        </Link>
        <button
          onClick={handleDismiss}
          className="text-[#0F172A]/60 hover:text-[#0F172A] p-1"
          aria-label="Dismiss reminder"
          data-testid="match-reminder-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default MatchReminderBanner;
